import { useMemo } from 'react';
import { useTribalContext } from '../context/tribal';
import {
  cubicCapacityToLitres,
  formatSpec,
  getEmissionsConsumptionValue,
  specIsInvalid,
} from '../modules/utils/storeUtils';
import { GlobalStoreStateConfiguration } from '../stores/globalStore';
import { fuelType } from '../modules/constants/fuelType';
import { formatCurrency } from '../modules/utils/currency';
import { filterUnique } from '../modules/utils/array';

interface useConfigurationSpecsProps {
  configuration: GlobalStoreStateConfiguration;
  invalidSpecFallback: string;
}

export const useConfigurationSpecs = (props: useConfigurationSpecsProps) => {
  const { configuration, invalidSpecFallback } = props;
  const { contentManager } = useTribalContext();
  
 const {
  pdbData: {
    technicalSpecifications: {
      engine: {
        fuelTypeName,
        cubicCapacity
      } = {},
      transmission: {
        driveTypeName,
        gearboxTypeName,
        numberOfGears
      } = {},
      performance: {
        acceleration,
        maxSpeed,
        power,
        torqueLb,
        torqueRpm
      } = {},
      dimensions: {
        width,
        height,
        length,
        numberOfDoors,
        bootVolume,
        bodyStyleName
      } = {},
      abiInsurance50,
    } = {}, // provide an empty object as default
    pricing: {
      firstYearFee
    } = {},
  } = {},
} = configuration as any;

  const getFuelTypeCharacter = () => {
    if (fuelTypeName === fuelType.ELECTRIC) {
      return '*';
    }

    if (fuelTypeName === fuelType.PLUG_IN_HYBRID) {
      return '**';
    }

    if (
      fuelTypeName === fuelType.HYBRID ||
      fuelTypeName === fuelType.DIESEL ||
      fuelTypeName === fuelType.PETROL
    ) {
      return '^';
    }

    return '';
  };

  const consumptionCombined = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'combined'
  );

  const consumptionCombinedSustaining = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'combinedSustaining'
  );

  const consumptionExtraHigh = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'extraHigh'
  );

  const consumptionHigh = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'high'
  );

  const consumptionMedium = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'medium'
  );

  const consumptionLow = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'low'
  );

  const energyCombined = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'energyCombined'
  );

  const electricBatteryRange = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'batteryRangeCombined'
  );

  const electricBatteryRangeCity = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'consumption',
    'batteryRangeCity'
  );

  const emissionsCombined = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'emissions',
    'combined'
  );

  const emissionsCombinedSustaining = getEmissionsConsumptionValue(
    configuration,
    fuelTypeName,
    'emissions',
    'combinedSustaining'
  );

  const engineAndTransmissionSpecsElectric = [
    {
      label: contentManager.get('battery-capacity-label'),
      text: formatSpec(
        configuration.pdbData?.technicalSpecifications?.battery?.capacity,
        invalidSpecFallback,
        ' kWh',
        true
      ),
    },
  ];

  const engineAndTransmissionSpecsNonElectric = [
    {
      label: contentManager.get('engine-label'),
      text: formatSpec(
        cubicCapacityToLitres(cubicCapacity),
        invalidSpecFallback,
        ' litres'
      ),
    },
  ];

  const engineAndTransmissionSpecsDiesel = [
    {
      label: contentManager.get('adblue-label'),
      text: 'Yes',
    },
  ];

  const consumptionSpecsPetrolDieselHybrid = [
    {
      label: contentManager.get('combined-label'),
      text: formatSpec(
        consumptionCombined?.value,
        invalidSpecFallback,
        ` ${consumptionCombined?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('extra-high-label'),
      text: formatSpec(
        consumptionExtraHigh?.value,
        invalidSpecFallback,
        ` ${consumptionExtraHigh?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('high-label'),
      text: formatSpec(
        consumptionHigh?.value,
        invalidSpecFallback,
        ` ${consumptionHigh?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('medium-label'),
      text: formatSpec(
        consumptionMedium?.value,
        invalidSpecFallback,
        ` ${consumptionMedium?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('low-label'),
      text: formatSpec(
        consumptionLow?.value,
        invalidSpecFallback,
        ` ${consumptionLow?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
  ];

  const energyConsumptionSpecsElectric = [
    {
      label: contentManager.get('combined-label'),
      text: formatSpec(
        consumptionCombined?.value,
        invalidSpecFallback,
        ` ${consumptionCombined?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
  ];

  const batteryRangeSpecsElectric = [
    {
      label: contentManager.get('combined-label'),
      text: formatSpec(
        electricBatteryRange?.value,
        invalidSpecFallback,
        ` ${electricBatteryRange?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('city-label'),
      text: formatSpec(
        electricBatteryRangeCity?.value,
        invalidSpecFallback,
        ` ${electricBatteryRangeCity?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
  ];

  const consumptionSpecsElectric = [
    {
      label: contentManager.get('energy-consumption-label'),
      text: formatSpec(
        consumptionCombined?.value,
        invalidSpecFallback,
        ` ${consumptionCombined?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('battery-range-label'),
      text: formatSpec(
        Math.round(electricBatteryRange?.value as number),
        invalidSpecFallback,
        ` ${electricBatteryRange?.unit}${getFuelTypeCharacter()}`,
        false
      ),
    },
    {
      label: contentManager.get('battery-range-city-label'),
      text: formatSpec(
        Math.round(electricBatteryRangeCity?.value as number),
        invalidSpecFallback,
        ` ${electricBatteryRangeCity?.unit}${getFuelTypeCharacter()}`,
        false
      ),
    },
    {
      label: contentManager.get('consumption-low'),
      text: formatSpec(
        consumptionLow?.value,
        invalidSpecFallback,
        ` ${consumptionLow?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('consumption-medium'),
      text: formatSpec(
        consumptionMedium?.value,
        invalidSpecFallback,
        ` ${consumptionMedium?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('consumption-high'),
      text: formatSpec(
        consumptionHigh?.value,
        invalidSpecFallback,
        ` ${consumptionHigh?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
    {
      label: contentManager.get('consumption-extra-high'),
      text: formatSpec(
        consumptionExtraHigh?.value,
        invalidSpecFallback,
        ` ${consumptionExtraHigh?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
  ];

  const fuelConsumptionSpecsPlugInHybrid = (shortenedLabels?: boolean) => [
    {
      label: shortenedLabels
        ? contentManager.get('combined-label')
        : contentManager.get('petrol-diesel-combined-label'),
      text: formatSpec(
        consumptionCombined?.value,
        invalidSpecFallback,
        ` ${consumptionCombined?.unit}${getFuelTypeCharacter()} (W)`,
        true
      ),
    },
    {
      label: shortenedLabels
        ? contentManager.get('combined-label')
        : contentManager.get('petrol-diesel-combined-label'),
      text: formatSpec(
        consumptionCombinedSustaining?.value,
        invalidSpecFallback,
        ` ${consumptionCombinedSustaining?.unit}${getFuelTypeCharacter()} (S)`,
        true
      ),
    },
    {
      label: shortenedLabels
        ? contentManager.get('extra-high-label')
        : contentManager.get('petrol-diesel-extra-high-label'),
      text: formatSpec(
        consumptionExtraHigh?.value,
        invalidSpecFallback,
        ` ${consumptionExtraHigh?.unit}${getFuelTypeCharacter()} (S)`,
        true
      ),
    },
    {
      label: shortenedLabels
        ? contentManager.get('high-label')
        : contentManager.get('petrol-diesel-high-label'),
      text: formatSpec(
        consumptionHigh?.value,
        invalidSpecFallback,
        ` ${consumptionHigh?.unit}${getFuelTypeCharacter()} (S)`,
        true
      ),
    },
    {
      label: shortenedLabels
        ? contentManager.get('medium-label')
        : contentManager.get('petrol-diesel-medium-label'),
      text: formatSpec(
        consumptionMedium?.value,
        invalidSpecFallback,
        ` ${consumptionMedium?.unit}${getFuelTypeCharacter()} (S)`,
        true
      ),
    },
    {
      label: shortenedLabels
        ? contentManager.get('low-label')
        : contentManager.get('petrol-diesel-low-label'),
      text: formatSpec(
        consumptionLow?.value,
        invalidSpecFallback,
        ` ${consumptionLow?.unit}${getFuelTypeCharacter()} (S)`,
        true
      ),
    },
  ];

  const energyConsumptionSpecsPlugInHybrid = [
    {
      label: contentManager.get('combined-label'),
      text: formatSpec(
        energyCombined?.value,
        invalidSpecFallback,
        ` ${energyCombined?.unit}${getFuelTypeCharacter()} (W)`,
        true
      ),
    },
  ];

  const consumptionSpecsPlugInHybrid = [
    ...fuelConsumptionSpecsPlugInHybrid(),
    {
      label: contentManager.get('energy-combined-label'),
      text: formatSpec(
        energyCombined?.value,
        invalidSpecFallback,
        ` ${energyCombined?.unit}${getFuelTypeCharacter()} (W)`,
        true
      ),
    },
    {
      label: contentManager.get('battery-range-city-label'),
      text: formatSpec(
        Math.round(electricBatteryRangeCity?.value as number),
        invalidSpecFallback,
        ` ${electricBatteryRangeCity?.unit}${getFuelTypeCharacter()}`,
        false
      ),
    },
    {
      label: contentManager.get('battery-range-label'),
      text: formatSpec(
        Math.round(electricBatteryRange?.value as number),
        invalidSpecFallback,
        ` ${electricBatteryRange?.unit}${getFuelTypeCharacter()}`,
        false
      ),
    },
  ];

  const emissionsSpecsPetrolDieselHybridElectric = [
    {
      label: contentManager.get('emissions-label'),
      text: formatSpec(
        emissionsCombined?.value,
        invalidSpecFallback,
        ` ${emissionsCombined?.unit}${getFuelTypeCharacter()}`,
        false
      ),
    },
  ];

  const emissionsSpecsPlugInHybrid = [
    {
      label: contentManager.get('emissions-label'),
      text: formatSpec(
        emissionsCombined?.value,
        invalidSpecFallback,
        ` ${emissionsCombined?.unit}${getFuelTypeCharacter()} (W)`,
        false
      ),
    },
    {
      label: contentManager.get('emissions-label'),
      text: formatSpec(
        emissionsCombinedSustaining?.value,
        invalidSpecFallback,
        ` ${emissionsCombinedSustaining?.unit}${getFuelTypeCharacter()} (S)`,
        false
      ),
    },
  ];

  const emissionsCombinedNonHybrid = [
    {
      label: contentManager.get('combined-label'),
      text: formatSpec(
        emissionsCombined?.value,
        invalidSpecFallback,
        ` ${emissionsCombined?.unit}${getFuelTypeCharacter()}`,
        true
      ),
    },
  ];

  const emissionsCombinedPlugInHybrid = [
    {
      label: contentManager.get('combined-label'),
      text: filterUnique([
        formatSpec(
          emissionsCombined?.value,
          invalidSpecFallback,
          ` ${emissionsCombined?.unit}${getFuelTypeCharacter()} (W)`,
          false
        ),
        formatSpec(
          emissionsCombinedSustaining?.value,
          invalidSpecFallback,
          ` ${emissionsCombinedSustaining?.unit}${getFuelTypeCharacter()} (S)`,
          false
        ),
      ]).join(' '),
    },
  ];

  return useMemo(
    () => ({
      getEngineAndTransmissionSpecs: () => {
        return [
          {
            label: contentManager.get('fuel-type-label'),
            text: formatSpec(fuelTypeName, invalidSpecFallback),
          },
          {
            label: contentManager.get('gearbox-label'),
            text: formatSpec(gearboxTypeName, invalidSpecFallback),
          },
          ...(fuelTypeName === fuelType.ELECTRIC
            ? engineAndTransmissionSpecsElectric
            : engineAndTransmissionSpecsNonElectric),
          {
            label: contentManager.get('wheel-drive-label'),
            text: formatSpec(driveTypeName, invalidSpecFallback),
          },
          {
            label: contentManager.get('transmission-label'),
            text: `${numberOfGears} speed ${gearboxTypeName}`,
          },
          ...(fuelTypeName === fuelType.DIESEL
            ? engineAndTransmissionSpecsDiesel
            : []),
        ];
      },

      getPerformanceSpecs: () => {
        return [
          {
            label: contentManager.get('acceleration-label'),
            text: formatSpec(acceleration, invalidSpecFallback, ' seconds'),
          },
          {
            label: contentManager.get('maximum-speed-label'),
            text: formatSpec(maxSpeed, invalidSpecFallback, 'mph'),
          },
          {
            label: contentManager.get('maximum-power-label'),
            text: formatSpec(power, invalidSpecFallback, 'ps'),
          },
          {
            label: contentManager.get('maximum-torque-label'),
            text:
              specIsInvalid(torqueLb) || specIsInvalid(torqueRpm)
                ? invalidSpecFallback
                : formatSpec(torqueLb, invalidSpecFallback, ' lbs.ft at ') +
                  formatSpec(torqueRpm, invalidSpecFallback, 'rpm'),
          },
        ];
      },

      getDimensionsSpecs: () => {
        return [
          {
            label: contentManager.get('width-label'),
            text: formatSpec(width, invalidSpecFallback, 'mm'),
          },
          {
            label: contentManager.get('height-label'),
            text: formatSpec(height, invalidSpecFallback, 'mm'),
          },
          {
            label: contentManager.get('length-label'),
            text: formatSpec(length, invalidSpecFallback, 'mm'),
          },
          {
            label: contentManager.get('doors-label'),
            text: formatSpec(numberOfDoors, invalidSpecFallback),
          },
          {
            label: contentManager.get('boot-space-label'),
            text: formatSpec(bootVolume, invalidSpecFallback, ' litres'),
          },
          {
            label: contentManager.get('style-label'),
            text: formatSpec(bodyStyleName, invalidSpecFallback),
          },
        ];
      },

      getConsumptionSpecs: () => {
        return [
          ...(fuelTypeName === fuelType.PETROL ||
          fuelTypeName === fuelType.DIESEL ||
          fuelTypeName === fuelType.HYBRID
            ? consumptionSpecsPetrolDieselHybrid
            : []),
          ...(fuelTypeName === fuelType.ELECTRIC
            ? consumptionSpecsElectric
            : []),
          ...(fuelTypeName === fuelType.PLUG_IN_HYBRID
            ? consumptionSpecsPlugInHybrid
            : []),
        ];
      },

      getEmissionsSpecs: () => {
        return [
          ...(fuelTypeName === fuelType.PLUG_IN_HYBRID
            ? emissionsSpecsPlugInHybrid
            : emissionsSpecsPetrolDieselHybridElectric),
        ];
      },

      getTaxAndInsuranceSpecs: () => {
        return [
          {
            label: contentManager.get('insurance-group-label'),
            text: formatSpec(abiInsurance50, invalidSpecFallback),
          },
          {
            label: contentManager.get('ved-label'),
            text: formatSpec(formatCurrency(firstYearFee), invalidSpecFallback),
          },
        ];
      },

      getFuelConsumptionSpecs: () => {
        return [
          ...(fuelTypeName === fuelType.PETROL ||
          fuelTypeName === fuelType.DIESEL ||
          fuelTypeName === fuelType.HYBRID
            ? consumptionSpecsPetrolDieselHybrid
            : []),
          ...(fuelTypeName === fuelType.PLUG_IN_HYBRID
            ? fuelConsumptionSpecsPlugInHybrid(true)
            : []),
        ];
      },

      getEnergyConsumptionSpecs: () => {
        return [
          ...(fuelTypeName === fuelType.ELECTRIC
            ? energyConsumptionSpecsElectric
            : fuelTypeName === fuelType.PLUG_IN_HYBRID
            ? energyConsumptionSpecsPlugInHybrid
            : []),
        ];
      },

      getBatteryRangeSpecs: () => {
        return [
          ...(fuelTypeName === fuelType.ELECTRIC
            ? batteryRangeSpecsElectric
            : []),
        ];
      },

      getEmissionsCombinedSpecs: () => {
        return [
          ...(fuelTypeName === fuelType.PLUG_IN_HYBRID
            ? emissionsCombinedPlugInHybrid
            : emissionsCombinedNonHybrid),
        ];
      },
    }),
    []
  );
};
